import './DictpickerModal.scss';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';
import DataTable from '@srm/ui/data-table';
import { invalidateDictionaryItems } from '@services/dictionaryCache';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IAddDictForm, IDictFilter, IExternalSearchDict } from '@models/Forms/IForms';
import { IDictSettings } from '../IDictSettings';
import { loadDictScheme } from '../loadDictScheme';
import ListModeControl from './ModeControls/ListModeControl';
import TreeModeControl from './ModeControls/TreeModeControl';
import { modalGridAttribute } from './ModeControls/dictGridColumns';
import DictExternalSearchModal from './DictExternalSearchModal';
import { IDictpickerRefActions } from '../IDictpickerRefActions';
import DictAddFormModal from './DictAddFormModal';
import { IFormData } from '@models/IFormData';
import ExternalSearchBarActionControl from '@controls/ExternalSearchBarActionControl';
import { portalOverlayClassName } from '@srm/ui/lib/portalOverlay';
import SelectedItemsPanel from '@srm/ui/selected-items-panel';
import { sameKeys } from '@srm/ui/lib/sameKeys';

export interface ISelectFromDictDialogProp extends IDictSettings {
    onClose: () => void;
    onSubmit: (data: IDictionaryData[]) => Promise<void>;
    onValidate?: (data: IDictionaryData[]) => Promise<boolean>;
    externalSearch?: IExternalSearchDict;
    addForm?: IAddDictForm;
}

const DictpickerModal: FC<ISelectFromDictDialogProp> = (props: ISelectFromDictDialogProp) => {
    const gridAttribute = useMemo(() => modalGridAttribute(props.gridAttribute), [props.gridAttribute]);
    const p = { ...props, gridAttribute };
    const [dictAttributes, setDictAttributes] = useState<string[]>();
    const [selectedKeys, setSelectedKeys] = useState<string[]>(p.selected);
    const [maxStructLevel, setMaxStructLevel] = useState<number>(0);
    const [filterResponse, setFilterResponse] = useState<IDictFilter>();
    const [selectedRowItems, setSelectedRowItems] = useState<IDictionaryData[]>([]);
    const [showExternalSearch, setShowExternalSearch] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const controlRef = useRef<IDictpickerRefActions>(null);
    const onSelectedRowItems = useCallback((value: IDictionaryData[]) => {
        setSelectedRowItems(value);
    }, []);
    const onSelectedKeys = useCallback((value: string[]) => {
        setSelectedKeys(value);
    }, []);

    useEffect(() => {
        if (dictAttributes) return;
        // фильтры (формулы формы) и схема друг от друга не зависят; схема — из кэша сессии
        void Promise.all([p.getFiltersAsync(), loadDictScheme(p.dictName, p.isFormData)]).then(([filters, scheme]) => {
            const attrs = [...scheme.attributes];
            if (!p.isFormData) {
                p.joinedDictionaries?.forEach((x) => {
                    x.outColumns.forEach((col) => attrs.push(col.name));
                });
                setMaxStructLevel(scheme.maxStructLevel);
            }
            setFilterResponse(filters);
            setDictAttributes(attrs);
        });
    }, [p.dictName]);

    const toggleShowExternalSearch = () => {
        setShowExternalSearch(!showExternalSearch);
    };

    const toggleShowAddForm = () => {
        setShowAddForm(!showAddForm);
    };

    const submittingRef = useRef(false);
    const submitSelection = async (items: IDictionaryData[], selectionComplete = true) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            if (p.onValidate && !(await p.onValidate(items))) return;
            if (selectionComplete) await p.onSubmit(items);
            p.onClose();
        } finally {
            submittingRef.current = false;
        }
    };
    const onConfirmRow = (row: IDictionaryData) => submitSelection([row]);

    // Подвал — элементом, не компонентом: функция-компонент, объявленная в рендере, — новый тип на каждый рендер, и
    // React монтировал подвал заново на каждый выбор строки
    const modalFooter = (
        <div className={'dictModal_footer'}>
            {p.externalSearch && (
                <div className="right">
                    <Button onClick={toggleShowExternalSearch} variant="light" size="sm">
                        {p.externalSearch.buttonName}
                    </Button>
                </div>
            )}

            {p.addForm && (
                <div className="right">
                    <Button onClick={toggleShowAddForm} variant="light" size="sm">
                        {p.addForm.buttonName}
                    </Button>
                </div>
            )}

            {p.showActionsBar && p.docId && (
                <div className="right">
                    <ExternalSearchBarActionControl docId={p.docId} />
                </div>
            )}

            <Button
                onClick={() => submitSelection(selectedRowItems, selectedKeys?.length === selectedRowItems.length)}
                size="sm"
            >
                Выбрать
            </Button>
            <Button onClick={p.onClose} variant="light" size="sm">
                Отменить
            </Button>
        </div>
    );

    const CheckHierarchical = useCallback(() => {
        return maxStructLevel > 0;
    }, [maxStructLevel]);

    const codeIsVisible =
        p.gridAttribute === undefined || p.gridAttribute.attrs.some((attribute) => attribute.key === 'code');
    const getSelectedItemLabel = useCallback(
        (item: IDictionaryData) => {
            const displayAttributeKeys = p.gridAttribute
                ? p.gridAttribute.attrs
                      .filter((attribute) => attribute.key !== 'code' && !attribute.joined)
                      .map((attribute) => attribute.key)
                : (dictAttributes ?? []);
            const displayValue = displayAttributeKeys
                .map((key) => {
                    const index = dictAttributes?.indexOf(key) ?? -1;
                    return index >= 0 ? item.fields?.[index]?.value : undefined;
                })
                .find((value) => value !== null && value !== undefined && String(value).trim().length > 0);

            if (displayValue !== undefined) return String(displayValue);
            if (codeIsVisible && item.code) return item.code;
            return 'Элемент без названия';
        },
        [codeIsVisible, dictAttributes, p.gridAttribute],
    );

    // Схема справочника и предустановленный отбор приходят запросами: пока их нет,
    // модалка уже открыта и показывает скелет списка — иначе клик по полю выглядит
    // как «ничего не произошло».
    const schemeReady = !!dictAttributes && !!filterResponse && maxStructLevel !== undefined;

    if (!schemeReady) {
        return (
            <Modal
                className={clsx('dictModal', portalOverlayClassName)}
                size="content"
                onClose={p.onClose}
                hasUnsavedInput={() => !sameKeys(selectedKeys, p.selected)}
                header={p.modalTitle ? p.modalTitle : p.dictName}
            >
                <div className="dict-list dict-list--pending">
                    <DataTable columns={[]} data={[]} getRowId={() => ''} loading />
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            className={clsx('dictModal', portalOverlayClassName)}
            size="content"
            onClose={p.onClose}
            hasUnsavedInput={() => !sameKeys(selectedKeys, p.selected)}
            footer={modalFooter}
            header={p.modalTitle ? p.modalTitle : p.dictName}
        >
            <div className="dictpicker-modal__layout">
                <div className="dictpicker-modal__grid">
                    {p.isFormData || !CheckHierarchical() ? (
                        <ListModeControl
                            onConfirmRow={onConfirmRow}
                            onSelectedRowItems={onSelectedRowItems}
                            onSelectedKeys={onSelectedKeys}
                            dictAttributes={dictAttributes}
                            filterResponse={filterResponse}
                            controlRef={controlRef}
                            {...p}
                        />
                    ) : (
                        <TreeModeControl
                            onConfirmRow={onConfirmRow}
                            onSelectedRowItems={onSelectedRowItems}
                            onSelectedKeys={onSelectedKeys}
                            maxStructLevel={maxStructLevel}
                            dictAttributes={dictAttributes}
                            filterResponse={filterResponse}
                            controlRef={controlRef}
                            {...p}
                        />
                    )}
                </div>
                <SelectedItemsPanel
                    className="selected-items-panel--picker-modal"
                    selectionMode={p.isMultiple ? 'multiple' : 'single'}
                    items={selectedRowItems}
                    getKey={(item) => item.fullId || item.id || item.code}
                    getPrefix={(item) =>
                        codeIsVisible && item.code && getSelectedItemLabel(item) !== item.code ? item.code : undefined
                    }
                    getLabel={getSelectedItemLabel}
                    onRemove={(item) => controlRef.current?.unSelect(item)}
                    onClear={() => controlRef.current?.clearSelection()}
                />
            </div>
            {showExternalSearch && (
                <DictExternalSearchModal
                    settings={p.externalSearch!}
                    onClose={() => {
                        toggleShowExternalSearch();
                    }}
                    onSubmit={(data: string) => {
                        controlRef.current?.setSelected([data]);
                    }}
                />
            )}
            {showAddForm && (
                <DictAddFormModal
                    settings={p.addForm!}
                    onClose={() => {
                        toggleShowAddForm();
                    }}
                    onSubmit={async (data: IFormData) => {
                        let code = '';
                        const codeField = data.fields.find((x) => x.name === 'code');
                        if (codeField && codeField.value) {
                            code = codeField.value.toString();
                        }

                        const dataDict: IDictionaryData = {
                            id: '',
                            code: code,
                            structure: '',
                            hasChild: false,
                            parent: '',
                            urlAttributes: [],
                            url: '',
                            fullId: '',
                            fields: data.fields,
                        };
                        invalidateDictionaryItems(p.dictName);
                        await p.onSubmit([dataDict]);

                        toggleShowAddForm();
                        p.onClose();
                    }}
                />
            )}
        </Modal>
    );
};

export default DictpickerModal;
